// Types for asset comparison functionality

import { DateRange } from './index'

// Data source metadata to distinguish real vs synthetic data
export interface ComparisonDataSourceMetadata {
  core_metrics?: 'real' | 'synthetic' | 'calculated'
  derived_metrics?: 'real' | 'synthetic' | 'calculated'
  trends?: 'real' | 'synthetic' | 'derived'
  baseline?: 'real' | 'synthetic'
  trend_points?: 'real' | 'synthetic'
  correlation_matrix?: 'real' | 'synthetic'
  note?: string
  [key: string]: string | undefined
}

// Filter types
export interface ComparisonFilters {
  dateRange: DateRange
  kpis: string[]
  sensors: string[]
  categories: string[]
}

// KPI data for a single asset
export interface AssetKPIData {
  asset_id: string
  name: string
  status: 'healthy' | 'warning' | 'critical'
  is_valid?: boolean  // New: Whether model meets quality threshold (AUC-ROC >= 0.6)
  model_auc_roc?: number  // New: AUC-ROC for model quality
  metrics: {
    reliability: number
    availability: number
    mtbf: number  // Mean Time Between Failures (hours)
    mttr: number  // Mean Time To Repair (hours)
    anomaly_rate: number
    health_score: number
    prediction_accuracy?: number
    oee?: number  // Overall Equipment Effectiveness
  }
  trends?: {
    reliability_trend: number  // Percentage change
    anomaly_trend: number
  }
  rank?: number
  data_source?: 'registry' | 'fallback'  // New: Indicates real vs fallback data
}

// KPI comparison response from API
export interface KPIComparisonData {
  assets: AssetKPIData[]
  best_performer: string
  worst_performer: string
  fleet_average: {
    reliability: number
    availability: number
    mtbf: number
    mttr: number
    anomaly_rate: number
  }
  summary: Record<string, number>
  invalid_models_excluded?: number  // New: Count of excluded invalid models
  timestamp: string
  data_source?: ComparisonDataSourceMetadata  // New: Overall data source metadata
}

// Trend comparison data
export interface TrendComparisonData {
  timestamps: string[]
  trends: Record<string, {
    reliability: number[]
    anomaly_score: number[]
    health_score: number[]
  }>
  timestamp: string
  data_source?: ComparisonDataSourceMetadata  // New: Overall data source metadata
}

// Correlation data
export interface CorrelationData {
  kpis: string[]
  matrix: number[][]
  assets: string[]
  timestamp: string
  correlations?: Record<string, {
    kpis: string[]
    matrix: number[][]
  }>
  data_source?: ComparisonDataSourceMetadata  // New: Overall data source metadata
}

// Executive summary for comparison
export interface ExecutiveSummaryComparison {
  asset_id: string
  period: string
  reliability: number
  downtime_hours: number
  cost_savings: number
  critical_events_prevented: number
  key_insights: string[]
  recommendations: string[]
}

// Comparison view modes
export type ComparisonViewMode = 'side-by-side' | 'overlay' | 'table'

// Sort options for comparison table
export interface ComparisonSortOptions {
  field: keyof AssetKPIData['metrics'] | 'name' | 'status'
  direction: 'asc' | 'desc'
}

// Available KPIs for selection
export interface KPIDefinition {
  id: string
  label: string
  unit: string
  description: string
  higherIsBetter: boolean
  category: 'reliability' | 'performance' | 'health' | 'anomaly'
}

export const AVAILABLE_KPIS: KPIDefinition[] = [
  { id: 'reliability', label: 'Reliability', unit: '%', description: 'Asset reliability score', higherIsBetter: true, category: 'reliability' },
  { id: 'availability', label: 'Availability', unit: '%', description: 'Operational availability', higherIsBetter: true, category: 'reliability' },
  { id: 'mtbf', label: 'MTBF', unit: 'hours', description: 'Mean Time Between Failures', higherIsBetter: true, category: 'performance' },
  { id: 'mttr', label: 'MTTR', unit: 'hours', description: 'Mean Time To Repair', higherIsBetter: false, category: 'performance' },
  { id: 'anomaly_rate', label: 'Anomaly Rate', unit: '%', description: 'Percentage of anomalous readings', higherIsBetter: false, category: 'anomaly' },
  { id: 'health_score', label: 'Health Score', unit: '%', description: 'Overall health indicator', higherIsBetter: true, category: 'health' },
]

// Default KPIs to show
export const DEFAULT_SELECTED_KPIS = ['reliability', 'availability', 'health_score', 'anomaly_rate']

// Mock data for fallback when API is unavailable
export const MOCK_COMPARISON_DATA: KPIComparisonData = {
  assets: [
    { asset_id: 'F67548', name: 'Compressor Unit 6', status: 'healthy', metrics: { reliability: 98.5, availability: 99.2, mtbf: 720, mttr: 2.5, anomaly_rate: 0.8, health_score: 95 }, rank: 1 },
    { asset_id: 'F70478', name: 'Compressor Unit 5', status: 'healthy', metrics: { reliability: 96.2, availability: 98.8, mtbf: 680, mttr: 3.1, anomaly_rate: 1.2, health_score: 92 }, rank: 2 },
    { asset_id: 'F69437', name: 'Compressor Unit 8', status: 'healthy', metrics: { reliability: 95.8, availability: 98.5, mtbf: 650, mttr: 3.4, anomaly_rate: 1.5, health_score: 91 }, rank: 3 },
    { asset_id: 'F70541', name: 'Compressor Unit 7', status: 'healthy', metrics: { reliability: 94.5, availability: 97.9, mtbf: 620, mttr: 4.2, anomaly_rate: 2.1, health_score: 88 }, rank: 4 },
    { asset_id: 'F68885', name: 'Compressor Unit 3', status: 'warning', metrics: { reliability: 88.2, availability: 94.5, mtbf: 480, mttr: 6.8, anomaly_rate: 4.5, health_score: 68 }, rank: 5 },
    { asset_id: 'F70231', name: 'Compressor Unit 4', status: 'warning', metrics: { reliability: 85.5, availability: 92.8, mtbf: 420, mttr: 8.2, anomaly_rate: 5.8, health_score: 72 }, rank: 6 },
    { asset_id: 'F69871', name: 'Compressor Unit 2', status: 'critical', metrics: { reliability: 78.2, availability: 88.5, mtbf: 320, mttr: 12.5, anomaly_rate: 8.2, health_score: 52 }, rank: 7 },
    { asset_id: 'F67503', name: 'Compressor Unit 1', status: 'critical', metrics: { reliability: 72.5, availability: 85.2, mtbf: 280, mttr: 15.8, anomaly_rate: 12.5, health_score: 45 }, rank: 8 },
  ],
  best_performer: 'F67548',
  worst_performer: 'F67503',
  fleet_average: {
    reliability: 88.7,
    availability: 94.4,
    mtbf: 521,
    mttr: 7.1,
    anomaly_rate: 4.6
  },
  summary: {
    total_assets: 8,
    healthy: 4,
    warning: 2,
    critical: 2
  },
  timestamp: new Date().toISOString()
}
